import { FC, useRef } from "react";
import { Identifier } from "dnd-core";
import { useDrag, useDrop, XYCoord } from "react-dnd";

import TestTableRow from "./TestTableRow";
import { HiddeableTest } from "./TipsTable";

interface DragItem {
  id: number;
  index: number;
}

interface Props {
  test: HiddeableTest;
  winning: boolean;
  index: number;
  reorderRows: (dragIndex: number, hoverIndex: number) => void;
  onDrop: () => void;
  groupId: number;
  hideThumbnail?: boolean;
  areThumbsEnlarged: boolean;
  setAreThumbsEnlarged: React.Dispatch<React.SetStateAction<boolean>>;
}

const DraggableTestRow: FC<Props> = ({
  index,
  reorderRows,
  onDrop,
  ...props
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: "TR",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    drop() {
      onDrop();
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();

      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      reorderRows(dragIndex, hoverIndex);

      item.index = hoverIndex;
    },
  });

  const [{ draggableTestId }, drag] = useDrag(
    () => ({
      type: "TR",
      item: () => ({
        id: props.test.id,
        index,
      }),
      collect: (monitor) => ({
        draggableTestId: monitor.getItem()?.id,
      }),
    }),
    [props.test]
  );

  drag(drop(ref));

  return (
    <TestTableRow
      {...props}
      ref={ref}
      handlerId={handlerId}
      isDragging={draggableTestId === props.test.id}
      isDraggable
    />
  );
};

export default DraggableTestRow;
